import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { restrictionsGroupBaseUrl } from '@/services/utils';
import {
  IBaaSRestrictionsGroup,
  IPAginateBaaSRestrictionGroup,
  ISearchRestrictionsGroupService,
  TRestrictionsGroupType,
} from '@/store/models/restriction.model';

import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';

import { fetchQuery } from '@/utils/queryClient';
import { fetchMethods } from '@/types/FetchService.types';

export class RestrictionsGroupService {
  static queryKey = 'Restriction Group';
  static searchRestrictionsGroups = async ({
    type,
    restrictionsGroupId,
    currentPage,
    pageCount,
    filters,
  }: ISearchRestrictionsGroupService): Promise<IPAginateBaaSRestrictionGroup> => {
    try {
      const config = getStaticDashboardConfig();

      const formattedFilter = filters
        ?.map((filter) => `${filter.fieldName} ${filter.criteria} ${filter.expression}`)
        ?.join(';');

      const search = {
        ...(!restrictionsGroupId && { pageNumber: currentPage, pageCount }),
        ...(formattedFilter && { filter: formattedFilter }),
      };

      const buildApiURL = new URL(
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}${
          restrictionsGroupId ? `/${restrictionsGroupId}` : ''
        }`,
      );

      if (search) {
        Object.keys(search).forEach((key) => buildApiURL.searchParams.append(key, search[key]));
      }

      const restrictionsGroupResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, id: restrictionsGroupId, params: { type, ...search } }],
        buildApiURL.toString(),
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse<IPAginateBaaSRestrictionGroup>(restrictionsGroupResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static addRestrictionsGroup = async (
    baaSRestrictionsGroup: IBaaSRestrictionsGroup,
    type: TRestrictionsGroupType,
  ): Promise<IBaaSRestrictionsGroup> => {
    try {
      const config = getStaticDashboardConfig();

      const restrictionsGroupResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.POST, id: baaSRestrictionsGroup.id, params: { type, baaSRestrictionsGroup } },
        ],
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(baaSRestrictionsGroup),
        },
      );
      const [restrictionsGroup] = await decryptTreezorResponse<IBaaSRestrictionsGroup>(restrictionsGroupResponse, {
        shouldSkipEncryption: true,
      });

      return restrictionsGroup;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateRestrictionsGroup = async (
    baaSRestrictionsGroup: IBaaSRestrictionsGroup,
    type: TRestrictionsGroupType,
  ): Promise<IBaaSRestrictionsGroup> => {
    try {
      const config = getStaticDashboardConfig();

      delete baaSRestrictionsGroup.createdDate;
      delete baaSRestrictionsGroup.modifiedDate;
      const { id, ...restrictionCard } = baaSRestrictionsGroup;

      const restrictionsGroupResponse = await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.PUT,
            id,
            params: {
              type,
              restrictionCard,
            },
          },
        ],
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}/${id}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ ...restrictionCard }),
        },
      );

      const [restrictionsGroup] = await decryptTreezorResponse<IBaaSRestrictionsGroup>(restrictionsGroupResponse, {
        shouldSkipEncryption: true,
      });
      return restrictionsGroup;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static deleteRestrictionsGroup = async (
    restrictionsGroupId: number,
    type: TRestrictionsGroupType,
  ): Promise<IBaaSRestrictionsGroup> => {
    try {
      const config = getStaticDashboardConfig();

      const restrictionsGroupResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.DELETE, id: restrictionsGroupId, params: { type } }],
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}/${restrictionsGroupId}`,
        {
          headers: createHttpHeader(),
        },
      );

      const [restrictionsGroup] = await decryptTreezorResponse<IBaaSRestrictionsGroup>(restrictionsGroupResponse, {
        shouldSkipEncryption: true,
      });
      return restrictionsGroup;
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
